import type {
  SelaCreateProjectRequest,
  SelaDealContextDates,
  SelaDealStage,
  SelaDevelopmentPath,
  SelaPriority,
} from '../services/selaApi';

export type V2OnboardingLocationMode = 'address' | 'parcel';

export type V2OnboardingState = {
  redirect?: string;
  address?: string;
  locationMode?: V2OnboardingLocationMode;
  parcelPin?: string;
  city?: string;
  state?: string;
  developmentPaths?: SelaDevelopmentPath[];
  dealStage?: SelaDealStage;
  priorities?: SelaPriority[];
  dealContextDates?: SelaDealContextDates;
  pendingCreate?: boolean;
};

const STORAGE_KEY = 'sela-v2-onboarding';

const AUTO_CREATE_REDIRECT = '/?onboarding=1&autoCreate=1';
const ONBOARDING_REDIRECT = '/?onboarding=1';

/** Rewrites legacy `/v2` URLs to `/` and keeps query strings. */
export function normalizeRedirectPath(redirect?: string | null): string {
  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return '/';
  }
  const qIdx = redirect.indexOf('?');
  const pathOnly = qIdx === -1 ? redirect : redirect.slice(0, qIdx);
  const search = qIdx === -1 ? '' : redirect.slice(qIdx);

  if (pathOnly === '/v2') {
    return `/${search}`;
  }
  if (pathOnly.startsWith('/v2/')) {
    return pathOnly.replace(/^\/v2/, '') + search;
  }
  return redirect;
}

function normalizeLocationMode(value?: string | null): V2OnboardingLocationMode | undefined {
  if (!value) {
    return undefined;
  }
  return value.toLowerCase() === 'parcel' ? 'parcel' : 'address';
}

function normalizeStoredState(parsed: V2OnboardingState): V2OnboardingState {
  return {
    redirect: normalizeRedirectPath(parsed.redirect),
    address: parsed.address?.trim() || '',
    locationMode: normalizeLocationMode(parsed.locationMode),
    parcelPin: parsed.parcelPin?.trim() || '',
    city: parsed.city?.trim() || '',
    state: parsed.state?.trim() || '',
    developmentPaths: Array.isArray(parsed.developmentPaths) ? parsed.developmentPaths : [],
    dealStage: parsed.dealStage,
    priorities: Array.isArray(parsed.priorities) ? parsed.priorities : [],
    dealContextDates: parsed.dealContextDates || {},
    pendingCreate: Boolean(parsed.pendingCreate),
  };
}

function applyParcelParams(params: URLSearchParams, state: V2OnboardingState): void {
  const mode = normalizeLocationMode(state.locationMode);
  if (mode === 'parcel') {
    params.set('locationMode', 'parcel');
    if (state.parcelPin?.trim()) {
      params.set('parcelPin', state.parcelPin.trim());
    }
    if (state.city?.trim()) {
      params.set('city', state.city.trim());
    }
    if (state.state?.trim()) {
      params.set('state', state.state.trim());
    }
  }
}

export function shouldPreserveOnboardingState(state: V2OnboardingState): boolean {
  return Boolean(state.pendingCreate);
}

export function buildOnboardingSearch(state: V2OnboardingState): string {
  const params = new URLSearchParams();
  const redirect = normalizeRedirectPath(state.redirect);
  const qIdx = redirect.indexOf('?');
  const pathname = qIdx === -1 ? redirect : redirect.slice(0, qIdx);
  const searchPart = qIdx === -1 ? '' : redirect.slice(qIdx + 1);
  const isDefaultHome = pathname === '/' && !searchPart;
  if (!isDefaultHome) {
    params.set('redirect', redirect);
  }
  if (state.address?.trim()) {
    params.set('address', state.address.trim());
  }
  applyParcelParams(params, state);
  const search = params.toString();
  return search ? `?${search}` : '';
}

export function buildOnboardingDestination(state: V2OnboardingState): string {
  if (state.pendingCreate) {
    const params = new URLSearchParams();
    params.set('onboarding', '1');
    params.set('autoCreate', '1');
    if (state.address?.trim()) {
      params.set('address', state.address.trim());
    }
    applyParcelParams(params, state);
    const search = params.toString();
    return search ? `/?${search}` : AUTO_CREATE_REDIRECT;
  }

  const redirect = normalizeRedirectPath(state.redirect);
  const q = redirect.indexOf('?');
  const pathname = q === -1 ? redirect : redirect.slice(0, q);
  const existingSearch = q === -1 ? '' : redirect.slice(q + 1);
  const params = new URLSearchParams(existingSearch);

  if (state.address?.trim()) {
    params.set('address', state.address.trim());
  }
  applyParcelParams(params, state);

  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
}

export function persistV2OnboardingState(state: V2OnboardingState): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeStoredState(state)));
}

export function readV2OnboardingState(): V2OnboardingState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as V2OnboardingState;
    return normalizeStoredState(parsed);
  } catch {
    return null;
  }
}

export function clearV2OnboardingState(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(STORAGE_KEY);
}

export function finalizeOnboardingAfterAuth(state: V2OnboardingState): void {
  if (!shouldPreserveOnboardingState(state)) {
    clearV2OnboardingState();
  }
}

export function resolveV2OnboardingState(search: string): V2OnboardingState {
  const params = new URLSearchParams(search || '');
  const queryState: V2OnboardingState = {
    redirect: params.get('redirect') || undefined,
    address: params.get('address') || undefined,
    locationMode: normalizeLocationMode(params.get('locationMode')),
    parcelPin: params.get('parcelPin') || undefined,
    city: params.get('city') || undefined,
    state: params.get('state') || undefined,
  };

  const storedState = readV2OnboardingState();
  const mergedMode =
    normalizeLocationMode(queryState.locationMode) ||
    normalizeLocationMode(storedState?.locationMode);

  return {
    redirect: normalizeRedirectPath(queryState.redirect || storedState?.redirect),
    address: queryState.address?.trim() || storedState?.address?.trim() || '',
    locationMode: mergedMode,
    parcelPin: queryState.parcelPin?.trim() || storedState?.parcelPin?.trim() || '',
    city: queryState.city?.trim() || storedState?.city?.trim() || '',
    state: queryState.state?.trim() || storedState?.state?.trim() || '',
    developmentPaths: storedState?.developmentPaths || [],
    dealStage: storedState?.dealStage,
    priorities: storedState?.priorities || [],
    dealContextDates: storedState?.dealContextDates || {},
    pendingCreate: storedState?.pendingCreate,
  };
}

function buildProjectName(address: string): string {
  const street = address.split(',')[0]?.trim() || address.trim();
  return `${street} Parcel Evaluation`.trim();
}

function buildParcelProjectName(parcelPin: string, city: string, state: string): string {
  const cityState = [city, state.toUpperCase()].filter(Boolean).join(', ');
  const head = cityState ? `Parcel ${parcelPin} (${cityState})` : `Parcel ${parcelPin}`;
  return `${head} Evaluation`.trim();
}

export function buildOnboardingDraft(input: {
  locationMode: V2OnboardingLocationMode;
  address?: string;
  parcelPin?: string;
  city?: string;
  state?: string;
  developmentPaths: SelaDevelopmentPath[];
  dealStage: SelaDealStage;
  priorities: SelaPriority[];
  dealContextDates: SelaDealContextDates;
  pendingCreate?: boolean;
}): V2OnboardingState {
  return {
    redirect: input.pendingCreate ? AUTO_CREATE_REDIRECT : ONBOARDING_REDIRECT,
    locationMode: input.locationMode,
    address: input.address?.trim() || '',
    parcelPin: input.parcelPin?.trim() || '',
    city: input.city?.trim() || '',
    state: input.state?.trim() || '',
    developmentPaths: input.developmentPaths,
    dealStage: input.dealStage,
    priorities: input.priorities,
    dealContextDates: input.dealContextDates,
    pendingCreate: input.pendingCreate,
  };
}

export function buildCreateProjectRequestFromState(
  state: V2OnboardingState,
): SelaCreateProjectRequest | null {
  const mode = normalizeLocationMode(state.locationMode) || 'address';
  const createPayload = {
    projectType: 'residential',
    evaluationMode: 'parcel_evaluation' as const,
    developmentPaths: state.developmentPaths || [],
    dealStage: state.dealStage || 'early_screening',
    priorities: state.priorities || [],
    dealContextDates: state.dealContextDates || {},
  };

  if (mode === 'parcel') {
    const pin = state.parcelPin?.trim();
    const city = state.city?.trim();
    const stateCode = state.state?.trim();
    if (!pin || !city || !stateCode) {
      return null;
    }
    return {
      projectName: buildParcelProjectName(pin, city, stateCode),
      ...createPayload,
      locationMode: 'parcel',
      parcelPin: pin,
      city,
      state: stateCode.toUpperCase(),
    };
  }

  const address = state.address?.trim();
  if (!address) {
    return null;
  }

  return {
    projectName: buildProjectName(address),
    ...createPayload,
    locationMode: 'address',
    propertyAddress: address,
  };
}
